import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useConfirm } from '../components/ui/ConfirmDialog.js';
import {
  Badge,
  Button,
  CursorPagination,
  Icon,
  Select,
  TextInput,
  cx,
} from '../components/ui/index.js';
import { readJson, useRemote } from '../lib/remote.js';
import { useAdminLocale } from '../lib/admin-locale.js';

interface SiteRow {
  id: string;
  name: string;
  canonicalOrigin: string;
}

interface InboxItem {
  id: string;
  siteId: string;
  siteName: string;
  origin: string;
  locale: string;
  status: string;
  startedAt: string;
  preview: string;
}

interface Transcript {
  id: string;
  siteId: string;
  siteName: string;
  origin: string;
  locale: string;
  status: string;
  startedAt: string;
  lead: { name: string; email: string; phone: string | null; message: string } | null;
  messages: Array<{
    id: string;
    role: string;
    text: string;
    createdAt: string;
    feedback: string | null;
    citations: Array<{ label: string; url: string | null; sourceId: string; sourceType: string }>;
  }>;
}

const LOCALE_LABEL: Record<string, { el: string; en: string }> = {
  el: { el: 'Ελληνικά', en: 'Greek' },
  en: { el: 'Αγγλικά', en: 'English' },
  fr: { el: 'Γαλλικά', en: 'French' },
};

const STATUS_LABEL: Record<string, { el: string; en: string }> = {
  OPEN: { el: 'Ανοιχτή', en: 'Open' },
  CLOSED: { el: 'Ολοκληρώθηκε', en: 'Completed' },
  EXPIRED: { el: 'Έληξε', en: 'Expired' },
};

const TYPE_LABEL: Record<string, { el: string; en: string }> = {
  WP_PAGE: { el: 'Σελίδα website', en: 'Website page' },
  WP_POST: { el: 'Άρθρο', en: 'Post' },
  PDF: { el: 'PDF', en: 'PDF' },
  FAQ: { el: 'Συχνή ερώτηση', en: 'FAQ' },
  TEXT: { el: 'Κείμενο', en: 'Text' },
};

type Period = 'all' | 'today' | 'week' | 'month';

function periodStart(period: Period): string {
  const now = new Date();
  if (period === 'today')
    return new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
  if (period === 'week') return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
  if (period === 'month') return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  return '';
}

function hostOf(origin: string): string {
  try {
    return new URL(origin).host;
  } catch {
    return origin;
  }
}

function localizedLabel(
  labels: Record<string, { el: string; en: string }>,
  value: string,
  text: (el: string, en: string) => string,
): string {
  const label = labels[value];
  return label ? text(label.el, label.en) : value;
}

function clock(value: string, locale: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

function when(value: string, locale: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' });
}

function statusDot(status: string): string {
  if (status === 'OPEN') return 'bg-emerald-500';
  if (status === 'CLOSED') return 'bg-slate-400';
  return 'bg-amber-500';
}

export function ConversationsInboxPage(props: { canWrite: boolean }) {
  const { locale: adminLocale, text } = useAdminLocale();
  const intlLocale = adminLocale === 'en' ? 'en-GB' : 'el-GR';
  const sites = useRemote<SiteRow[]>('/v1/sites');
  const [params, setParams] = useSearchParams();
  const siteId = params.get('site') ?? '';
  const selectedId = params.get('id') ?? '';
  const [period, setPeriod] = useState<Period>('month');
  const [locale, setLocale] = useState('');
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const search = new URLSearchParams();
  if (siteId) search.set('siteId', siteId);
  if (locale) search.set('locale', locale);
  const from = periodStart(period);
  if (from) search.set('from', from);
  if (query.trim()) search.set('q', query.trim());
  if (cursor) search.set('cursor', cursor);
  const inbox = useRemote<{ items: InboxItem[]; nextCursor: string | null }>(
    `/v1/conversations?${search.toString()}`,
  );
  const items = inbox.data?.items ?? [];
  const selected = items.find((item) => item.id === selectedId) ?? null;
  const transcript = useRemote<Transcript>(
    selected ? `/v1/sites/${selected.siteId}/conversations/${selected.id}` : null,
  );

  useEffect(() => {
    if (!inbox.data) return;
    if (items.some((item) => item.id === selectedId)) return;
    const next = new URLSearchParams(params);
    if (items[0]) next.set('id', items[0].id);
    else next.delete('id');
    if ((next.get('id') ?? '') === selectedId) return;
    setParams(next, { replace: true });
  }, [inbox.data, items, params, selectedId, setParams]);

  function setSite(value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set('site', value);
    else next.delete('site');
    next.delete('id');
    setCursor(null);
    setParams(next);
  }

  function choose(item: InboxItem): void {
    const next = new URLSearchParams(params);
    next.set('id', item.id);
    setParams(next);
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 admin-surface p-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative min-w-0 flex-1 sm:max-w-sm">
          <Icon
            name="chat"
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400 dark:text-slate-500"
          />
          <TextInput
            value={query}
            onChange={(event) => {
              setCursor(null);
              setQuery(event.target.value);
            }}
            placeholder={text('Αναζήτηση συνομιλίας…', 'Search conversations…')}
            aria-label={text('Αναζήτηση συνομιλίας', 'Search conversations')}
            flush
            className="pl-9"
          />
        </div>
        <Select
          aria-label={text('Ιστοσελίδα', 'Website')}
          className="w-full shrink-0 sm:w-52"
          value={siteId}
          onChange={setSite}
          options={[
            { value: '', label: text('Όλες οι ιστοσελίδες', 'All websites') },
            ...(sites.data ?? []).map((site) => ({
              value: site.id,
              label: hostOf(site.canonicalOrigin),
            })),
          ]}
        />
        <Select
          aria-label={text('Ημερομηνία', 'Date')}
          className="w-full shrink-0 sm:w-48"
          value={period}
          onChange={(next) => {
            setCursor(null);
            setPeriod(next as Period);
          }}
          options={[
            { value: 'today', label: text('Σήμερα', 'Today') },
            { value: 'week', label: text('Τελευταίες 7 ημέρες', 'Last 7 days') },
            { value: 'month', label: text('Αυτός ο μήνας', 'This month') },
            { value: 'all', label: text('Όλες οι ημερομηνίες', 'All dates') },
          ]}
        />
        <Select
          aria-label={text('Γλώσσα', 'Language')}
          className="w-full shrink-0 sm:w-44"
          value={locale}
          onChange={(next) => {
            setCursor(null);
            setLocale(next);
          }}
          options={[
            { value: '', label: text('Όλες οι γλώσσες', 'All languages') },
            { value: 'el', label: text('Ελληνικά', 'Greek') },
            { value: 'en', label: text('Αγγλικά', 'English') },
            { value: 'fr', label: text('Γαλλικά', 'French') },
          ]}
        />
      </div>

      <div className="grid items-stretch gap-4 xl:h-[calc(100vh-12rem)] xl:grid-cols-[20rem_minmax(0,1fr)_19rem]">
        <aside className="flex min-h-80 flex-col overflow-hidden admin-surface xl:min-h-0">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-800">
            <div>
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {text('Συνομιλίες', 'Conversations')}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {inbox.data
                  ? text(
                      `${items.length}${inbox.data.nextCursor ? '+' : ''} σε αυτή τη σελίδα`,
                      `${items.length}${inbox.data.nextCursor ? '+' : ''} on this page`,
                    )
                  : text('Φόρτωση…', 'Loading…')}
              </p>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {inbox.error ? (
              <p className="p-4 text-sm text-rose-700 dark:text-rose-300">{inbox.error.message}</p>
            ) : null}
            {!inbox.data && !inbox.error ? (
              <p className="p-4 text-sm text-slate-500 dark:text-slate-400">
                {text('Φόρτωση…', 'Loading…')}
              </p>
            ) : null}
            {inbox.data && items.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
                <span className="grid size-12 place-items-center rounded-full bg-slate-100 text-slate-400 dark:bg-slate-800">
                  <Icon name="chat" className="size-5" />
                </span>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                  {text('Καμία συνομιλία', 'No conversations')}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {text(
                    'Δοκιμάστε άλλα φίλτρα ή περιμένετε νέες ερωτήσεις από το widget.',
                    'Try different filters or wait for new questions from the widget.',
                  )}
                </p>
              </div>
            ) : null}
            <ul>
              {items.map((item) => {
                const active = item.id === selectedId;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => choose(item)}
                      className={cx(
                        'flex w-full items-start gap-3 border-b border-slate-100 px-4 py-3 text-left transition dark:border-slate-800',
                        active
                          ? 'bg-blue-50 dark:bg-blue-950/40'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-800/80',
                      )}
                    >
                      <span className="relative mt-0.5 grid size-10 shrink-0 place-items-center rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        <Icon name="users" className="size-4" />
                        <span
                          className={cx(
                            'absolute right-0 bottom-0 size-2.5 rounded-full ring-2 ring-white dark:ring-slate-900',
                            statusDot(item.status),
                          )}
                          title={localizedLabel(STATUS_LABEL, item.status, text)}
                        />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-start justify-between gap-2">
                          <span
                            className={cx(
                              'line-clamp-2 text-sm',
                              active
                                ? 'font-semibold text-slate-900 dark:text-slate-100'
                                : 'font-medium text-slate-800 dark:text-slate-200',
                            )}
                          >
                            {item.preview ||
                              text('Συνομιλία χωρίς μήνυμα', 'Conversation without a message')}
                          </span>
                          <span className="shrink-0 text-[11px] text-slate-400 dark:text-slate-500">
                            {clock(item.startedAt, intlLocale)}
                          </span>
                        </span>
                        <span className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                          <span className="rounded-md bg-slate-100 px-1.5 py-0.5 font-semibold tracking-wide text-slate-600 uppercase dark:bg-slate-800 dark:text-slate-300">
                            {item.locale}
                          </span>
                          <span className="truncate">{hostOf(item.origin)}</span>
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
          <CursorPagination
            hasNext={Boolean(inbox.data?.nextCursor)}
            isFirst={!cursor}
            onFirst={() => setCursor(null)}
            onNext={() => setCursor(inbox.data?.nextCursor ?? null)}
          />
        </aside>

        <article className="flex min-h-80 flex-col overflow-hidden admin-surface xl:min-h-0">
          {!selected ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-16 text-center">
              <span className="grid size-14 place-items-center rounded-2xl bg-slate-100 text-slate-400 dark:bg-slate-800">
                <Icon name="inbox" className="size-6" />
              </span>
              <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                {text('Επιλέξτε συνομιλία', 'Select a conversation')}
              </p>
              <p className="max-w-xs text-xs text-slate-500 dark:text-slate-400">
                {text(
                  'Από τη λίστα αριστερά ανοίγετε το transcript και τις πηγές απάντησης.',
                  'Select from the list on the left to open the transcript and answer sources.',
                )}
              </p>
            </div>
          ) : (
            <>
              <header className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-800">
                <div className="min-w-0">
                  <h3 className="truncate text-base font-semibold text-slate-900 dark:text-slate-100">
                    {selected.preview || text('Συνομιλία', 'Conversation')}
                  </h3>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                    <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 dark:bg-slate-800">
                      <Icon name="globe" className="size-3.5" />
                      {hostOf(selected.origin)}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 dark:bg-slate-800">
                      {localizedLabel(LOCALE_LABEL, selected.locale, text)}
                    </span>
                    <span>{when(selected.startedAt, intlLocale)}</span>
                  </div>
                </div>
                <Badge
                  value={selected.status}
                  label={localizedLabel(STATUS_LABEL, selected.status, text)}
                />
              </header>
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-slate-50/80 px-5 py-5 dark:bg-slate-950/40">
                {transcript.error ? (
                  <p className="text-sm text-rose-700 dark:text-rose-300">
                    {transcript.error.message}
                  </p>
                ) : null}
                {!transcript.data && !transcript.error ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {text('Φόρτωση συνομιλίας…', 'Loading conversation…')}
                  </p>
                ) : null}
                {transcript.data && transcript.data.messages.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-16 text-center">
                    <span className="grid size-12 place-items-center rounded-full bg-white text-slate-400 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
                      <Icon name="chat" className="size-5" />
                    </span>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {text('Χωρίς μηνύματα ακόμη', 'No messages yet')}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {text(
                        'Η συνομιλία άνοιξε, αλλά δεν έχει καταγραφεί κείμενο.',
                        'The conversation was opened, but no text has been recorded.',
                      )}
                    </p>
                  </div>
                ) : null}
                {transcript.data?.messages.map((message) => {
                  const visitor = message.role === 'USER';
                  return (
                    <div
                      key={message.id}
                      className={cx('flex gap-2', visitor ? 'justify-end' : 'justify-start')}
                    >
                      {!visitor ? (
                        <span className="mt-1 grid size-8 shrink-0 place-items-center rounded-full bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900">
                          <Icon name="pulse" className="size-3.5" />
                        </span>
                      ) : null}
                      <div
                        className={cx(
                          'max-w-[min(36rem,85%)] px-4 py-3 text-sm shadow-sm',
                          visitor
                            ? 'rounded-2xl rounded-br-md bg-blue-600 text-white'
                            : 'rounded-2xl rounded-bl-md border border-slate-200 bg-white text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200',
                        )}
                      >
                        <p className="leading-6 whitespace-pre-wrap">{message.text}</p>
                        <div
                          className={cx(
                            'mt-1.5 flex flex-wrap items-center gap-2 text-[11px]',
                            visitor ? 'text-blue-100' : 'text-slate-400 dark:text-slate-500',
                          )}
                        >
                          <span>{clock(message.createdAt, intlLocale)}</span>
                          {message.feedback === 'HELPFUL' ? (
                            <span>· {text('χρήσιμη', 'helpful')}</span>
                          ) : null}
                          {message.feedback === 'NOT_HELPFUL' ? (
                            <span>· {text('μη χρήσιμη', 'not helpful')}</span>
                          ) : null}
                        </div>
                        {message.citations.length > 0 ? (
                          <ul
                            className={cx(
                              'mt-2 space-y-1 border-t pt-2',
                              visitor
                                ? 'border-blue-500/40'
                                : 'border-slate-100 dark:border-slate-800',
                            )}
                          >
                            {message.citations.map((citation) => (
                              <li key={citation.sourceId}>
                                <Link
                                  to={`/sites/${selected.siteId}/sources/${citation.sourceId}`}
                                  className={cx(
                                    'inline-flex items-center gap-1 text-xs font-medium',
                                    visitor
                                      ? 'text-blue-50 hover:text-white'
                                      : 'text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200',
                                  )}
                                >
                                  <Icon name="file" className="size-3.5" />
                                  {citation.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </article>

        <ConversationFacts
          item={selected}
          transcript={transcript.data}
          canWrite={props.canWrite}
          onDeleted={() => {
            const next = new URLSearchParams(params);
            next.delete('id');
            setParams(next);
            void inbox.mutate();
          }}
        />
      </div>
    </section>
  );
}

function ConversationFacts(props: {
  item: InboxItem | null;
  transcript: Transcript | undefined;
  canWrite: boolean;
  onDeleted: () => void;
}) {
  const { locale, text } = useAdminLocale();
  const intlLocale = locale === 'en' ? 'en-GB' : 'el-GR';
  const confirmDialog = useConfirm();
  const [error, setError] = useState<string | null>(null);
  const data = props.transcript;
  const sources = new Map<string, { label: string; sourceType: string }>();
  for (const message of data?.messages ?? []) {
    for (const citation of message.citations) {
      if (!sources.has(citation.sourceId)) {
        sources.set(citation.sourceId, { label: citation.label, sourceType: citation.sourceType });
      }
    }
  }

  return (
    <aside className="flex min-h-0 flex-col gap-4 xl:overflow-y-auto">
      <section className="overflow-hidden admin-surface">
        <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {text('Λεπτομέρειες', 'Details')}
          </h3>
        </div>
        {!props.item || !data ? (
          <p className="px-4 py-5 text-sm text-slate-500 dark:text-slate-400">
            {text(
              'Επιλέξτε συνομιλία για στοιχεία, πηγές και επαφή.',
              'Select a conversation to view details, sources, and contact information.',
            )}
          </p>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            <dl className="space-y-3 px-4 py-4 text-sm">
              <Fact icon="globe" label="Website" value={hostOf(data.origin)} />
              <Fact
                icon="chat"
                label={text('Γλώσσα', 'Language')}
                value={localizedLabel(LOCALE_LABEL, data.locale, text)}
              />
              <Fact
                icon="pulse"
                label={text('Έναρξη', 'Started')}
                value={when(data.startedAt, intlLocale)}
              />
              <Fact
                icon="mail"
                label={text('Μηνύματα', 'Messages')}
                value={String(data.messages.length)}
              />
            </dl>

            <div className="px-4 py-4">
              <h4 className="text-xs font-semibold tracking-wide text-slate-500 uppercase dark:text-slate-400">
                {text('Πηγές απαντήσεων', 'Answer sources')}
              </h4>
              {sources.size === 0 ? (
                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                  {text(
                    'Καμία παραπομπή σε αυτή τη συνομιλία.',
                    'No citations in this conversation.',
                  )}
                </p>
              ) : (
                <ul className="mt-2 space-y-2">
                  {[...sources.entries()].map(([id, source]) => (
                    <li key={id}>
                      <Link
                        to={`/sites/${props.item?.siteId}/sources/${id}`}
                        className="flex items-start gap-2 rounded-lg px-2 py-1.5 text-sm text-blue-700 hover:bg-slate-50 dark:text-blue-300 dark:hover:bg-slate-800"
                      >
                        <Icon name="file" className="mt-0.5 size-4 shrink-0" />
                        <span>
                          <span className="block font-medium">{source.label}</span>
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            {localizedLabel(TYPE_LABEL, source.sourceType, text)}
                          </span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="px-4 py-4">
              <h4 className="text-xs font-semibold tracking-wide text-slate-500 uppercase dark:text-slate-400">
                {text('Επαφή', 'Contact')}
              </h4>
              {data.lead ? (
                <dl className="mt-2 space-y-2 text-sm">
                  <div>
                    <dt className="text-xs text-slate-500 dark:text-slate-400">
                      {text('Όνομα', 'Name')}
                    </dt>
                    <dd className="font-medium text-slate-900 dark:text-slate-100">
                      {data.lead.name}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500 dark:text-slate-400">Email</dt>
                    <dd className="font-medium text-slate-900 dark:text-slate-100">
                      {data.lead.email}
                    </dd>
                  </div>
                  {data.lead.phone ? (
                    <div>
                      <dt className="text-xs text-slate-500 dark:text-slate-400">
                        {text('Τηλέφωνο', 'Phone')}
                      </dt>
                      <dd className="font-medium text-slate-900 dark:text-slate-100">
                        {data.lead.phone}
                      </dd>
                    </div>
                  ) : null}
                  {data.lead.message ? (
                    <div>
                      <dt className="text-xs text-slate-500 dark:text-slate-400">
                        {text('Μήνυμα', 'Message')}
                      </dt>
                      <dd className="whitespace-pre-wrap text-slate-800 dark:text-slate-200">
                        {data.lead.message}
                      </dd>
                    </div>
                  ) : null}
                </dl>
              ) : (
                <p className="mt-2 flex items-start gap-2 text-sm text-slate-500 dark:text-slate-400">
                  <Icon name="users" className="mt-0.5 size-4 shrink-0" />
                  {text(
                    'Δεν έχουν καταχωριστεί στοιχεία επικοινωνίας.',
                    'No contact details have been recorded.',
                  )}
                </p>
              )}
            </div>
          </div>
        )}
      </section>

      {props.canWrite && props.item ? (
        <div>
          {error ? <p className="mb-2 text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}
          <Button
            type="button"
            variant="danger"
            className="w-full"
            onClick={() => {
              if (!props.item) return;
              const item = props.item;
              void (async () => {
                const ok = await confirmDialog({
                  title: text('Διαγραφή συνομιλίας', 'Delete conversation'),
                  message: text('Διαγραφή αυτής της συνομιλίας;', 'Delete this conversation?'),
                  confirmLabel: text('Διαγραφή', 'Delete'),
                  danger: true,
                });
                if (!ok) return;
                setError(null);
                try {
                  await readJson(`/v1/sites/${item.siteId}/conversations/${item.id}`, {
                    method: 'DELETE',
                  });
                  props.onDeleted();
                } catch (caught: unknown) {
                  setError(
                    caught instanceof Error
                      ? caught.message
                      : text('Η διαγραφή απέτυχε', 'Deletion failed'),
                  );
                }
              })();
            }}
          >
            <Icon name="trash" className="size-4" />{' '}
            {text('Διαγραφή συνομιλίας', 'Delete conversation')}
          </Button>
        </div>
      ) : null}
    </aside>
  );
}

function Fact(props: { icon: 'globe' | 'chat' | 'pulse' | 'mail'; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <Icon
        name={props.icon}
        className="mt-0.5 size-4 shrink-0 text-slate-400 dark:text-slate-500"
      />
      <div>
        <dt className="text-xs text-slate-500 dark:text-slate-400">{props.label}</dt>
        <dd className="font-medium text-slate-900 dark:text-slate-100">{props.value}</dd>
      </div>
    </div>
  );
}
